<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, ref, watch } from 'vue';

import { Type } from './types';

export interface Props {
  label?: string;
  open?: boolean;
  collapsible?: boolean;
  stickyTop?: string;
  type?: Type;
}

const props = withDefaults(defineProps<Props>(), {
  open: true,
  collapsible: true,
  stickyTop: '0px',
});

const emit = defineEmits<{ toggle: [{ value: boolean }] }>();
const isOpen = ref(props.open);
watch(
  () => props.open,
  (v) => (isOpen.value = v),
);

const getIcon = computed(() => {
  switch (props.type) {
    case Type.Danger:
      return 'fa-solid fa-exclamation-circle';
    case Type.Warning:
      return 'fa-solid fa-exclamation-triangle';
    case Type.Info:
      return 'fa-solid fa-info-circle';
    default:
      return '';
  }
});

function toggle() {
  if (!props.collapsible) return;
  isOpen.value = !isOpen.value;
  emit('toggle', { value: true });
}
</script>

<template>
  <div
    class="rounded-lg"
    :class="[
      {
        'bg-info-200': type === Type.Info,
        'bg-red-50': type === Type.Danger,
        'bg-orange-200': type === Type.Warning,
        'bg-grey-100': type === undefined,
      },
    ]"
    data-testid="collapse"
  >
    <div
      v-if="collapsible || !!label"
      class="sticky z-5 flex cursor-pointer items-center justify-between gap-3 p-3 font-bold"
      :class="{
        collapsible,
        'rounded-t-lg': isOpen,
        'rounded-lg': !isOpen,
        'bg-info-300 text-info': type === Type.Info,
        'bg-red-100 text-red-500': type === Type.Danger,
        'bg-orange-300 text-orange': type === Type.Warning,
        'bg-grey-200': type === undefined,
      }"
      :style="{ top: stickyTop }"
      @click="toggle"
      data-testid="collapse-trigger"
    >
      <span class="flex items-center gap-2">
        <FontAwesomeIcon v-if="type" :icon="getIcon" />
        <span class="label-txt">{{ label }}</span>
      </span>
      <span v-if="collapsible" class="flex items-center gap-2">
        <span class="show-more"
          ><span v-if="isOpen">toon minder</span><span v-else>toon meer</span></span
        >
        <span>
          <FontAwesomeIcon v-if="isOpen" icon="fa-solid fa-chevron-up" />
          <FontAwesomeIcon v-else icon="fa-solid fa-chevron-down" />
        </span>
      </span>
      <slot name="header"></slot>
    </div>

    <div
      :class="[
        'p-4',
        {
          hidden: !isOpen,
        },
      ]"
      data-testid="collapse-content"
    >
      <slot></slot>
    </div>
  </div>
</template>
